<template>
  <header
    id="header"
    class="sticky top-0 z-10 transition-all duration-200"
    :class="{
      'max-md:-translate-y-full': shouldHideHeader,
      'md:-translate-y-full': shouldHideHeaderOnMd,
    }"
  >
    <div class="flex w-full">
      <div
        ref="header"
        class="bg-static-default-low border-static-default-low relative z-[1] w-full border-b-1 transition-all duration-200"
      >
        <ErrorOwners v-if="bannerEnabled" :owners="['bot-squad-content-front']">
          <PromotionalBanner
            :class="{
              hidden: !displayPromotionalBannerOnPage,
              'hidden md:block':
                isSearchBarActive && displayPromotionalBannerOnPage,
            }"
            data-test="promotional-banner-wrapper"
          />
        </ErrorOwners>
        <Banner
          class="relative z-[2] hidden md:flex"
          :class="[
            displayPromotionalBannerOnPage
              ? 'bg-static-default-low'
              : 'bg-surface-default-hi',
          ]"
        />
        <Main />
        <NavMenu class="hidden md:block" />
      </div>
    </div>
    <!-- we only render the NavBurgerMenu after hydration is completed to avoid unnecessary execution initially -->
    <NavBurgerMenu v-if="hydrationCompleted" v-show="isSidebarOpened" />
    <ClientOnly>
      <CountrySwitchRedirection
        v-if="displaySwitchBannerOnPage"
        ref="countrySwitchBannerRef"
      />
    </ClientOnly>
  </header>
</template>

<script setup lang="ts">
import { useRoute, useRuntimeConfig } from '#imports'
import { computed } from 'vue'

import { useIsHydrationCompleted } from '@backmarket/nuxt-layer-base/composables/useIsHydrationComplete'
import PromotionalBanner from '@backmarket/nuxt-layer-cms/PromotionalBanner.vue'
import { useAddToCartPopover } from '@backmarket/nuxt-layer-recommendation/useAddToCartPopover'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import ErrorOwners from '@backmarket/nuxt-module-logger/components/ErrorOwners.vue'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'

import { CHECKOUT as CHECKOUT_ROUTES } from '~/scopes/checkout/routes-names'
import { ROUTE_NAMES as DASHBOARD_ROUTES } from '~/scopes/dashboard/constants'
import { useToggleSearchBar } from '~/scopes/search-bar/composables/useToggleSearchBar'

import { useActiveMenu } from '../../composables/useActiveMenu'
import { useBurgerMenuState } from '../../composables/useBurgerMenuState'
import { useHideOnScroll } from '../../composables/useHideOnScroll'
import CountrySwitchRedirection from '../CountrySwitch/CountrySwitchRedirection.vue'
import NavBurgerMenu from '../NavBurgerMenu/NavBurgerMenu.vue'
import NavMenu from '../NavMenu/NavMenu.vue'

import Banner from './components/Banner.vue'
import Main from './components/Main.vue'

const hydrationCompleted = useIsHydrationCompleted()

const { hidden: isHeaderHidden } = useHideOnScroll()
const { isOpen: isSidebarOpened } = useBurgerMenuState()
const { isMenuActive } = useActiveMenu()
const { isSearchBarActive } = useToggleSearchBar()
const { isVisible: isAddToCartPopoverVisible } = useAddToCartPopover()
const experiments = useExperiments()
const route = useRoute()
const country = useMarketplace().market.countryCode
const {
  public: { FF_CMS_ENABLE_PROMOTIONAL_BANNER_COUNTRIES },
} = useRuntimeConfig()

const bannerEnabled =
  FF_CMS_ENABLE_PROMOTIONAL_BANNER_COUNTRIES?.includes(country)

const hasCountrySwitchRedirection =
  experiments['experiment.countrySwitchRedirection'] === 'withRedirection'

const hasLocaleSwitchExperiment =
  experiments['experiment.enableEsUsLocaleSelector'] !== 'disabled'

const displayPromotionalBannerOnPage = computed(() => {
  if (!route.name || !bannerEnabled) return false

  const { DASHBOARD } = DASHBOARD_ROUTES

  const hideBannerRoutes = [
    DASHBOARD.SELF,
    DASHBOARD.FAVORITES,
    DASHBOARD.MY_ORDERS.SELF,
    DASHBOARD.MY_ORDERS.WITHDRAW.INSURANCES,
    DASHBOARD.MY_ORDERS.SELECT_YOUR_ORDERLINE,
    DASHBOARD.MY_ORDERS.TRACK_PARCEL,
    DASHBOARD.MY_SALES,
    DASHBOARD.KYC.OTHER,
    DASHBOARD.KYC.PROFILE,
    DASHBOARD.KYC.DELETE_ACCOUNT,
    DASHBOARD.KYC.REFER_FRIEND,
    DASHBOARD.SERVICES_HUB,
    DASHBOARD.POLICY_DETAILS,
  ]

  return !hideBannerRoutes.includes(route.name.toString())
})

const displaySwitchBannerOnPage = computed(() => {
  if (!route.name) return false
  if (!hasCountrySwitchRedirection && !hasLocaleSwitchExperiment) return false

  const excludedRoutes = [CHECKOUT_ROUTES.PAYMENT_RESULT]

  return !excludedRoutes.includes(route.name.toString())
})

const shouldHideHeader = computed(
  () =>
    isHeaderHidden.value &&
    !isSearchBarActive.value &&
    !isSidebarOpened.value &&
    !isMenuActive.value,
)

const shouldHideHeaderOnMd = computed(
  () => shouldHideHeader.value && !isAddToCartPopoverVisible.value,
)
</script>
